import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SEO from '../../../components/SEO';
import { blogPosts } from '../../../mocks/blogPosts';
import { blogPostContents } from '../../../mocks/blogPostContents';

const BASE_URL = 'https://alliancesecuritysystems.com';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [serviceAreasOpen, setServiceAreasOpen] = useState(false);
  const [mobileServiceAreasOpen, setMobileServiceAreasOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);

  const post = blogPosts.find((p) => p.slug === slug);
  const content = slug ? blogPostContents[slug] : null;

  // Redirect if post not found
  useEffect(() => {
    if (!post) {
      navigate('/blog', { replace: true });
    }
  }, [post, navigate]);

  // Fade‑in animation
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  // Scroll to top on slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Reading progress & header shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const el = document.getElementById('article-body');
      if (el) {
        const rect = el.getBoundingClientRect();
        const total = el.offsetHeight;
        const scrolled = Math.max(0, -rect.top);
        setReadingProgress(Math.min(100, Math.round((scrolled / total) * 100)));
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const relatedPosts = useMemo(() => {
    if (!post) return [];
    return blogPosts
      .filter(
        (p) =>
          p.id !== post.id &&
          (p.category === post.category ||
            p.tags.some((t) => post.tags.includes(t)))
      )
      .slice(0, 3);
  }, [post]);

  const categoryColors: Record<string, string> = {
    'Security Tips': 'bg-emerald-100 text-emerald-700',
    'Product Reviews': 'bg-amber-100 text-amber-700',
    'Industry News': 'bg-sky-100 text-sky-700',
    'Case Studies': 'bg-rose-100 text-rose-700',
    'How-To Guides': 'bg-violet-100 text-violet-700',
    'Commercial Security': 'bg-orange-100 text-orange-700',
  };

  const serviceAreas = [
    { name: 'Toronto', path: '/service-areas/toronto' },
    { name: 'Mississauga', path: '/service-areas/mississauga' },
    { name: 'Brampton', path: '/service-areas/brampton' },
    { name: 'Oakville', path: '/service-areas/oakville' },
    { name: 'Hamilton', path: '/service-areas/hamilton' },
    { name: 'Richmond Hill', path: '/service-areas/richmond-hill' },
    { name: 'Vaughan', path: '/service-areas/vaughan' },
  ];

  // Guard against missing data
  if (!post || !content) return null;

  const pageUrl = `${BASE_URL}/blog/${post.slug}`;
  const datePublished = new Date(post.date).toISOString().split('T')[0];
  const dateModified = post.lastUpdated
    ? new Date(post.lastUpdated).toISOString().split('T')[0]
    : datePublished;
  const wordCount = content.sections.reduce(
    (acc, s) =>
      acc +
      s.body.split(' ').length +
      (s.bullets?.reduce((a, b) => a + b.split(' ').length, 0) ?? 0),
    0
  );

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(pageUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = pageUrl;
        textarea.style.position = 'fixed';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          setCopied(true);
        } catch (err) {
          console.error('Copy failed', err);
        }
        document.body.removeChild(textarea);
        setTimeout(() => setCopied(false), 2000);
      });
  };

  // Structured data for SEO
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': pageUrl,
    headline: post.title,
    description: post.excerpt,
    image: {
      '@type': 'ImageObject',
      url: post.image,
      width: 800,
      height: 500,
    },
    thumbnailUrl: post.image,
    datePublished,
    dateModified,
    author: {
      '@type': 'Person',
      name: post.author,
      jobTitle: post.authorRole,
      worksFor: {
        '@type': 'Organization',
        name: 'Alliance Security Systems',
      },
    },
    publisher: {
      '@type': 'Organization',
      name: 'Alliance Security Systems',
      url: BASE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
    keywords: post.tags.join(', '),
    articleSection: post.category,
    wordCount,
    inLanguage: 'en-CA',
    isAccessibleForFree: true,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', '.speakable', '.key-takeaways'],
    },
    about: [
      { '@type': 'Thing', name: 'Security Systems' },
      { '@type': 'Thing', name: post.category },
      ...post.tags.map((t) => ({ '@type': 'Thing', name: t })),
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: BASE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${BASE_URL}/blog`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.title,
        item: pageUrl,
      },
    ],
  };

  const faqItems = content.sections
    .filter((s) => s.heading && s.body)
    .map((s) => ({
      question: s.heading.endsWith('?')
        ? s.heading
        : `What about ${s.heading.toLowerCase()}?`,
      answer: s.body + (s.bullets ? ' ' + s.bullets.join('. ') : ''),
    }));

  const structuredData = [articleSchema, breadcrumbSchema];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${post.title} | Alliance Security Systems Blog`}
        description={post.excerpt}
        keywords={`${post.tags.join(', ')}, ${post.category}, security blog, GTA security`}
        canonical={`/blog/${post.slug}`}
        ogImage={post.image}
        ogType="article"
        structuredData={structuredData}
        faqItems={faqItems}
      />

      {/* Reading progress bar */}
      <div
        className="fixed top-0 left-0 h-0.5 bg-[#D4A853] z-[60] transition-all duration-150"
        style={{ width: `${readingProgress}%` }}
        role="progressbar"
        aria-valuenow={readingProgress}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Reading progress"
      />

      {/* Navigation */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'
        }`}
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-4 py-2.5 bg-[#0A1628]">
              <span
                className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                ALLIANCE
              </span>
              <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">
                SECURITY SYSTEMS
              </span>
            </div>
          </Link>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
          >
            <i
              className={`${
                mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
              } text-2xl`}
            ></i>
          </button>

          <div className="hidden md:flex items-center gap-6">
            {[
              { label: 'Cameras', path: '/camera-packages' },
              { label: 'Access Control', path: '/access-control' },
              { label: 'Alarm Packages', path: '/alarm-packages' },
              { label: 'Live Monitoring', path: '/live-monitoring' },
              { label: 'Clients', path: '/clients' },
              { label: 'FAQ', path: '/faq' },
              { label: 'Blog', path: '/blog' },
              { label: 'Contact Us', path: '/contact' },
            ].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  item.path === '/blog'
                    ? 'text-[#D4A853]'
                    : 'text-gray-700 hover:text-[#D4A853]'
                }`}
              >
                {item.label}
              </Link>
            ))}

            <div
              className="relative"
              onMouseEnter={() => setServiceAreasOpen(true)}
              onMouseLeave={() => setServiceAreasOpen(false)}
            >
              <button className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap">
                Service Areas
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    serviceAreasOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {serviceAreasOpen && (
                <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                  {serviceAreas.map((area) => (
                    <Link
                      key={area.path}
                      to={area.path}
                      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#D4A853]/10 hover:text-[#D4A853] transition-colors cursor-pointer"
                    >
                      {area.name}
                    </Link>
                  ))}
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <Link
                      to="/service-areas"
                      className="block px-4 py-2.5 text-sm font-semibold text-[#D4A853] hover:bg-[#D4A853]/10 transition-colors cursor-pointer"
                    >
                      View All Areas →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
            >
              Get Free Quote
            </Link>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-2">
            {[
              { label: 'Camera Packages', path: '/camera-packages' },
              { label: 'Access Control', path: '/access-control' },
              { label: 'Alarm Packages', path: '/alarm-packages' },
              { label: 'Live Monitoring', path: '/live-monitoring' },
              { label: 'Clients', path: '/clients' },
              { label: 'FAQ', path: '/faq' },
              { label: 'Blog', path: '/blog' },
              { label: 'Contact Us', path: '/contact' },
            ].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
              >
                {item.label}
              </Link>
            ))}
            <div className="border-b border-gray-50">
              <button
                onClick={() => setMobileServiceAreasOpen(!mobileServiceAreasOpen)}
                className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] cursor-pointer"
              >
                Service Areas
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    mobileServiceAreasOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {mobileServiceAreasOpen && (
                <div className="pl-4 pb-3 space-y-1">
                  {serviceAreas.map((area) => (
                    <Link
                      key={area.path}
                      to={area.path}
                      className="block py-2 text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer"
                    >
                      {area.name}
                    </Link>
                  ))}
                  <Link
                    to="/service-areas"
                    className="block py-2 text-sm font-semibold text-[#D4A853] cursor-pointer"
                  >
                    View All Areas →
                  </Link>
                </div>
              )}
            </div>
            <Link
              to="/contact"
              className="block w-full text-center bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2"
            >
              Get Free Quote
            </Link>
          </div>
        )}
      </nav>

      {/* Hero */}
      <header
        className="relative bg-[#0A1628] overflow-hidden"
        style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.7s ease' }}
      >
        <div className="absolute inset-0">
          <img
            src={post.image}
            alt={`${post.title} - Alliance Security Systems Blog`}
            title={`${post.title} - ${post.category} - GTA Security`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/95 via-[#0A1628]/70 to-[#0A1628]/40"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-16 pb-20 sm:pt-20 sm:pb-28">
          {/* Breadcrumb */}
          <nav
            className="flex items-center gap-2 text-white/60 text-xs mb-6"
            aria-label="Breadcrumb"
          >
            <ol
              className="flex items-center gap-2"
              itemScope
              itemType="https://schema.org/BreadcrumbList"
            >
              <li
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
              >
                <Link
                  to="/"
                  className="hover:text-white transition-colors cursor-pointer"
                  itemProp="item"
                >
                  <span itemProp="name">Home</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <li>
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
              >
                <Link
                  to="/blog"
                  className="hover:text-white transition-colors cursor-pointer"
                  itemProp="item"
                >
                  <span itemProp="name">Blog</span>
                </Link>
                <meta itemProp="position" content="2" />
              </li>
              <li>
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
              >
                <span
                  className="text-white/40 truncate max-w-[200px]"
                  itemProp="name"
                >
                  {post.title}
                </span>
                <meta itemProp="position" content="3" />
              </li>
            </ol>
          </nav>

          <div className="flex items-center gap-3 mb-5">
            <span
              className={`text-xs font-bold px-3 py-1.5 rounded-full ${
                categoryColors[post.category] ??
                'bg-gray-100 text-gray-700'
              }`}
            >
              {post.category}
            </span>
            <span className="text-white/50 text-xs flex items-center gap-1">
              <i className="ri-time-line"></i> {post.readTime} min read
            </span>
            <span className="text-white/50 text-xs flex items-center gap-1">
              <i className="ri-file-text-line"></i> {wordCount.toLocaleString()} words
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight mb-6 speakable">
            {post.title}
          </h1>

          <p className="text-white/75 text-base sm:text-lg leading-relaxed mb-8 max-w-2xl speakable">
            {post.excerpt}
          </p>

          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-[#D4A853]">
              <img
                src={post.authorImage}
                alt={`${post.author} - ${post.authorRole}`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">{post.author}</p>
              <p className="text-white/55 text-xs">{post.authorRole}</p>
            </div>
            <div className="ml-4 pl-4 border-l border-white/20">
              <p className="text-white/55 text-xs flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                <time dateTime={datePublished}>{post.date}</time>
              </p>
            </div>
            {post.lastUpdated && (
              <div className="pl-4 border-l border-white/20">
                <p className="flex items-center gap-1.5 text-xs font-medium bg-emerald-500/20 text-emerald-300 px-2.5 py-1 rounded-full whitespace-nowrap">
                  <i className="ri-refresh-line"></i>
                  <time dateTime={dateModified}>Updated: {post.lastUpdated}</time>
                </p>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Article Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 flex flex-col lg:flex-row gap-12">
        {/* Main Article */}
        <article
          id="article-body"
          className="flex-1 min-w-0"
          itemScope
          itemType="https://schema.org/BlogPosting"
        >
          <meta itemProp="headline" content={post.title} />
          <meta itemProp="datePublished" content={datePublished} />
          <meta itemProp="dateModified" content={dateModified} />
          <meta itemProp="author" content={post.author} />
          <meta itemProp="image" content={post.image} />
          <meta itemProp="description" content={post.excerpt} />

          {/* Key Takeaways */}
          <div className="bg-[#FBF7EF] border-l-4 border-[#D4A853] rounded-xl p-6 mb-6 key-takeaways speakable">
            <h2 className="text-sm font-bold text-[#0A1628] uppercase tracking-wider mb-3 flex items-center gap-2">
              <i className="ri-lightbulb-line text-[#D4A853]"></i> Key Takeaways
            </h2>
            <ul className="space-y-2">
              {content.keyTakeaways.map((point, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm text-gray-700"
                >
                  <i className="ri-check-line text-[#D4A853] mt-0.5 flex-shrink-0"></i>
                  <strong>{point}</strong>
                </li>
              ))}
            </ul>
          </div>

          {/* Last Reviewed note */}
          <div className="flex items-center gap-2.5 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 mb-10 w-fit">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-line text-emerald-500 text-sm"></i>
            </span>
            <span>
              <span className="font-semibold text-gray-600">Last reviewed:</span>{' '}
              <time dateTime={dateModified}>
                {post.lastUpdated ?? post.date}
              </time>
              {' '}— Verified accurate by the Alliance Security Systems editorial team.
            </span>
          </div>

          {/* Article Sections */}
          <div className="prose-custom space-y-10" itemProp="articleBody">
            {content.sections.map((section, i) => (
              <section key={i} id={`section-${i}`}>
                <h2 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-[#D4A853]/15 rounded-lg flex-shrink-0">
                    <i
                      className={`${section.icon} text-[#D4A853] text-base`}
                    ></i>
                  </span>
                  <a
                    href={`#section-${i}`}
                    className="hover:text-[#D4A853] transition-colors"
                  >
                    {section.heading}
                  </a>
                </h2>
                <p className="text-gray-600 leading-relaxed text-[15px] mb-4 speakable">
                  {section.body}
                </p>
                {section.bullets && section.bullets.length > 0 && (
                  <ul className="space-y-2 mt-3">
                    {section.bullets.map((b, bi) => (
                      <li
                        key={bi}
                        className="flex items-start gap-2.5 text-sm text-gray-600"
                      >
                        <i className="ri-arrow-right-s-line text-[#D4A853] mt-0.5 flex-shrink-0 text-base"></i>
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                {section.callout && (
                  <div className="mt-5 bg-[#0A1628]/5 border border-[#0A1628]/10 rounded-xl p-5">
                    <p className="text-sm text-[#0A1628] font-medium italic leading-relaxed speakable">
                      <i className="ri-double-quotes-l text-[#D4A853] mr-1"></i>
                      {section.callout}
                      <i className="ri-double-quotes-r text-[#D4A853] ml-1"></i>
                    </p>
                  </div>
                )}
              </section>
            ))}
          </div>

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Tags
            </p>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs bg-[#F8F9FA] text-gray-600 px-3 py-1.5 rounded-full border border-gray-200 hover:border-[#D4A853] hover:text-[#D4A853] transition-colors cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {/* Share */}
          <div className="mt-8 pt-8 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
              Share this article
            </p>
            <div className="flex items-center gap-3">
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  pageUrl
                )}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F8F9FA] border border-gray-200 text-gray-600 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-all cursor-pointer"
                aria-label="Share on Facebook"
              >
                <i className="ri-facebook-fill text-base"></i>
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                  pageUrl
                )}&text=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F8F9FA] border border-gray-200 text-gray-600 hover:bg-black hover:text-white hover:border-black transition-all cursor-pointer"
                aria-label="Share on X"
              >
                <i className="ri-twitter-x-line text-base"></i>
              </a>
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                  pageUrl
                )}&title=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F8F9FA] border border-gray-200 text-gray-600 hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-all cursor-pointer"
                aria-label="Share on LinkedIn"
              >
                <i className="ri-linkedin-fill text-base"></i>
              </a>
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#F8F9FA] border border-gray-200 text-gray-600 hover:border-[#D4A853] hover:text-[#D4A853] transition-all cursor-pointer text-xs font-medium whitespace-nowrap"
              >
                <i className={copied ? 'ri-check-line' : 'ri-link'}></i>
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>

          {/* Author Card */}
          <div
            className="mt-10 bg-[#F8F9FA] rounded-2xl p-6 flex items-start gap-5 border border-gray-100"
            itemScope
            itemType="https://schema.org/Person"
          >
            <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 border-[#D4A853]">
              <img
                src={post.authorImage}
                alt={`${post.author} - ${post.authorRole} at Alliance Security Systems`}
                className="w-full h-full object-cover object-top"
                itemProp="image"
              />
            </div>
            <div>
              <p className="text-xs font-bold text-[#D4A853] uppercase tracking-wider mb-1">
                Written by
              </p>
              <h3 className="text-base font-bold text-[#0A1628] mb-1" itemProp="name">
                {post.author}
              </h3>
              <p className="text-xs text-gray-500 mb-2" itemProp="jobTitle">
                {post.authorRole} — Alliance Security Systems
              </p>
              <p className="text-sm text-gray-600 leading-relaxed speakable" itemProp="description">
                {content.authorBio}
              </p>
            </div>
          </div>
        </article>

        {/* Sidebar */}
        <aside className="lg:w-80 flex-shrink-0 space-y-8">
          <div className="bg-[#F8F9FA] rounded-2xl p-6 border border-gray-100 sticky top-24">
            <h3 className="text-sm font-bold text-[#0A1628] uppercase tracking-wider mb-4 flex items-center gap-2">
              <i className="ri-list-check text-[#D4A853]"></i> In This Article
            </h3>
            <nav className="space-y-2" aria-label="Table of contents">
              {content.sections.map((section, i) => (
                <a
                  key={i}
                  href={`#section-${i}`}
                  className="flex items-start gap-2.5 text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer group"
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(`section-${i}`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  <span className="w-5 h-5 flex items-center justify-center bg-[#D4A853]/15 rounded text-[10px] font-bold text-[#D4A853] flex-shrink-0 mt-0.5 group-hover:bg-[#D4A853] group-hover:text-white transition-all">
                    {i + 1}
                  </span>
                  <span className="leading-snug">{section.heading}</span>
                </a>
              ))}
            </nav>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                Need a security assessment for your property?
              </p>
              <Link
                to="/contact"
                className="block w-full text-center bg-[#D4A853] text-white px-4 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
              >
                Get Free Quote
              </Link>
              <a
                href="tel:18884589181"
                className="block w-full text-center mt-2 border border-gray-200 text-gray-600 px-4 py-3 rounded-full text-sm font-medium hover:border-[#D4A853] hover:text-[#D4A853] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-1.5"></i> 1-888-458-9181
              </a>
            </div>
          </div>
        </aside>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-[#F8F9FA] py-14 sm:py-20" aria-label="Related articles">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0A1628]">
                Related Articles
              </h2>
              <Link
                to="/blog"
                className="text-sm font-semibold text-[#D4A853] hover:underline cursor-pointer flex items-center gap-1 whitespace-nowrap"
              >
                View All <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map((rp) => (
                <article
                  key={rp.id}
                  className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col"
                  itemScope
                  itemType="https://schema.org/BlogPosting"
                >
                  <Link
                    to={`/blog/${rp.slug}`}
                    className="block w-full h-44 overflow-hidden flex-shrink-0 cursor-pointer"
                  >
                    <img
                      src={rp.image}
                      alt={`${rp.title} - Alliance Security Systems Blog`}
                      title={`${rp.title} - ${rp.category}`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                      itemProp="image"
                    />
                  </Link>
                  <div className="p-5 flex flex-col flex-1">
                    <span
                      className={`self-start text-xs font-bold px-3 py-1 rounded-full mb-3 ${
                        categoryColors[rp.category] ?? 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {rp.category}
                    </span>
                    <Link to={`/blog/${rp.slug}`} className="cursor-pointer">
                      <h4
                        className="text-base font-bold text-[#0A1628] mb-2 leading-snug hover:text-[#D4A853] transition-colors line-clamp-2"
                        itemProp="headline"
                      >
                        {rp.title}
                      </h4>
                    </Link>
                    <p
                      className="text-sm text-gray-500 line-clamp-2 mb-4 flex-1"
                      itemProp="description"
                    >
                      {rp.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <i className="ri-time-line"></i> {rp.readTime} min read
                      </span>
                      <Link
                        to={`/blog/${rp.slug}`}
                        className="text-xs font-semibold text-[#D4A853] hover:underline cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        Read More <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Banner */}
      <section
        className="bg-gradient-to-r from-[#0A1628] to-[#1a2d4a] py-14 sm:py-20 mx-4 sm:mx-6 rounded-2xl my-10"
        aria-label="Free security assessment"
      >
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="inline-block bg-[#D4A853] text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide mb-5 whitespace-nowrap">
            Free Security Assessment
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-4">
            Ready to Protect Your Property?
          </h2>
          <p className="text-white/70 text-base mb-8 max-w-xl mx-auto">
            Our certified security experts will assess your property and
            recommend the perfect solution — at no cost to you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="bg-[#D4A853] text-white px-8 py-3.5 rounded-full font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg"
            >
              Get Free Quote
            </Link>
            <a
              href="tel:18884589181"
              className="border-2 border-white/40 text-white px-8 py-3.5 rounded-full font-semibold hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line"></i> 1-888-458-9181
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white pt-12 pb-8 mx-3 sm:mx-6 rounded-t-[24px] sm:rounded-t-[32px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-10">
            <div>
              <div className="inline-block border-2 border-[#D4A853] rounded-lg px-4 py-2.5 bg-[#0A1628] mb-4">
                <div className="flex flex-col leading-none">
                  <span
                    className="text-xl font-extrabold tracking-wider text-[#D4A853]"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    ALLIANCE
                  </span>
                  <span className="text-[8px] font-semibold tracking-[0.25em] text-[#D4A853]">
                    SECURITY SYSTEMS
                  </span>
                </div>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Trusted Security Since 1999
              </p>
              <a
                href="tel:18884589181"
                className="inline-block bg-[#D4A853] text-white px-5 py-2.5 rounded-full text-xs font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>1-888-458-9181
              </a>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-4 text-white">Quick Links</h3>
              <div className="space-y-2 text-sm">
                {[
                  { label: 'Home', path: '/' },
                  { label: 'Blog', path: '/blog' },
                  { label: 'FAQ', path: '/faq' },
                  { label: 'Service Areas', path: '/service-areas' },
                  { label: 'Contact Us', path: '/contact' },
                ].map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="block text-white/60 hover:text-[#D4A853] transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-4 text-white">Head Office</h3>
              <div className="space-y-2 text-sm text-white/60">
                <p>2355 Derry Road East, Unit 1</p>
                <p>Mississauga, ON L5S 1V6</p>
                <a
                  href="tel:9056960613"
                  className="block hover:text-[#D4A853] transition-colors cursor-pointer"
                >
                  (905) 696-0613
                </a>
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-xs">
              © 2024 Alliance Security Systems. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/privacy-policy"
                className="text-white/40 hover:text-white text-xs transition-colors cursor-pointer"
              >
                Privacy Policy
              </Link>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-white text-xs transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
